import { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";

// @mui material components
import Grid from "@mui/material/Grid";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import Icon from "@mui/material/Icon";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import CircularProgress from "@mui/material/CircularProgress";

// Material Kit 2 React components
import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";
import MKButton from "components/MKButton";
import MKAlert from "components/MKAlert";

// API
import { adminCreateTestPayment, adminGetBookings, adminUpdateBooking } from "api";

const statusColors = {
  pending: "warning",
  approved: "info",
  confirmed: "success",
  rejected: "error",
};

function BookingCard({ booking, onAction, actionLoading }) {
  const {
    bookingId,
    status,
    checkIn,
    checkOut,
    guestInfo,
    pricing,
    createdAt,
    paymentStatus,
    isTestPayment,
    paymentEvents,
  } = booking;
  const latestPaymentEvent =
    Array.isArray(paymentEvents) && paymentEvents.length > 0
      ? paymentEvents[paymentEvents.length - 1]
      : null;

  return (
    <MKBox
      border="1px solid"
      borderColor="grey.300"
      borderRadius="xl"
      p={2.5}
      mb={2}
      sx={{ "&:hover": { boxShadow: 3 }, transition: "box-shadow 0.2s" }}
    >
      <MKBox display="flex" justifyContent="space-between" alignItems="flex-start" mb={1.5}>
        <MKBox>
          <MKTypography variant="h6" mb={0.5}>
            {guestInfo.name}
          </MKTypography>
          <MKTypography variant="caption" fontWeight="bold" color="dark">
            {guestInfo.email} &middot; {guestInfo.countryCode || ""} {guestInfo.phone}
          </MKTypography>
        </MKBox>
        <MKBox display="flex" gap={1} alignItems="center">
          {isTestPayment && (
            <Chip label="test" color="default" size="small" sx={{ textTransform: "uppercase" }} />
          )}
          <Chip
            label={status}
            color={statusColors[status] || "default"}
            size="small"
            sx={{ textTransform: "capitalize" }}
          />
        </MKBox>
      </MKBox>

      <Grid container spacing={2} mb={1.5}>
        <Grid item xs={12} sm={6}>
          <MKTypography variant="body2" fontWeight="bold" color="dark">
            <strong>Dates:</strong> {checkIn} → {checkOut} ({pricing.nights} nights)
          </MKTypography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <MKTypography variant="body2" fontWeight="bold" color="dark">
            <strong>Total:</strong> ${pricing.total.toLocaleString()} &middot;{" "}
            <strong>Deposit:</strong> ${pricing.minimumDeposit.toLocaleString()}
          </MKTypography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <MKTypography variant="body2" fontWeight="bold" color="dark">
            <strong>Guests:</strong> {guestInfo.guests}
            {guestInfo.children && guestInfo.children !== "0"
              ? ` (${guestInfo.children} children)`
              : ""}{" "}
            &middot; <strong>Payment:</strong> {paymentStatus}
          </MKTypography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <MKTypography variant="caption" fontWeight="bold" color="dark">
            Requested: {new Date(createdAt).toLocaleDateString()}
          </MKTypography>
        </Grid>
      </Grid>

      {guestInfo.specialRequests && (
        <MKTypography
          variant="body2"
          fontWeight="bold"
          color="dark"
          mb={1.5}
          sx={{ fontStyle: "italic" }}
        >
          &ldquo;{guestInfo.specialRequests}&rdquo;
        </MKTypography>
      )}

      {latestPaymentEvent && (
        <MKBox bgColor="grey-100" borderRadius="lg" p={1.5} mb={1.5}>
          <MKTypography variant="caption" fontWeight="bold" color="dark" display="block" mb={0.5}>
            Latest payment event — {new Date(latestPaymentEvent.timestamp).toLocaleString()}
          </MKTypography>
          <MKTypography
            variant="body2"
            fontWeight="bold"
            color={latestPaymentEvent.status === "error" ? "error" : "dark"}
            mb={0.25}
          >
            {latestPaymentEvent.message}
          </MKTypography>
          <MKTypography variant="caption" color="text" display="block">
            {latestPaymentEvent.stage}
            {latestPaymentEvent.issue ? ` · ${latestPaymentEvent.issue}` : ""}
            {latestPaymentEvent.orderStatus ? ` · ${latestPaymentEvent.orderStatus}` : ""}
            {latestPaymentEvent.debugId ? ` · Debug ID: ${latestPaymentEvent.debugId}` : ""}
          </MKTypography>
        </MKBox>
      )}

      {status === "pending" && (
        <MKBox display="flex" gap={1} mt={1}>
          <MKButton
            variant="gradient"
            color="success"
            size="small"
            disabled={actionLoading}
            onClick={() => onAction(bookingId, "approve")}
          >
            <Icon sx={{ mr: 0.5 }}>check</Icon>
            Approve &amp; Send Payment Link
          </MKButton>
          <MKButton
            variant="outlined"
            color="error"
            size="small"
            disabled={actionLoading}
            onClick={() => onAction(bookingId, "reject")}
          >
            <Icon sx={{ mr: 0.5 }}>close</Icon>
            Reject
          </MKButton>
        </MKBox>
      )}

      {status !== "pending" && (
        <MKBox display="flex" gap={1} mt={1}>
          <MKButton
            variant="text"
            color="error"
            size="small"
            disabled={actionLoading}
            onClick={() => onAction(bookingId, "delete")}
          >
            <Icon sx={{ mr: 0.5 }}>delete</Icon>
            Delete
          </MKButton>
        </MKBox>
      )}
    </MKBox>
  );
}

function AdminBookings({ token, onAuthError }) {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);
  const [creatingTestLink, setCreatingTestLink] = useState(false);
  const [testPaymentLink, setTestPaymentLink] = useState(null);

  const loadBookings = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await adminGetBookings(token);
      setBookings(data.bookings || []);
    } catch (err) {
      if (err.message?.includes("Unauthorized")) {
        onAuthError();
        return;
      }
      setError(err.message || "Failed to load bookings");
    } finally {
      setLoading(false);
    }
  }, [token, onAuthError]);

  useEffect(() => {
    loadBookings();
  }, [loadBookings]);

  const handleAction = useCallback(async (bookingId, action) => {
    setConfirmAction({ bookingId, action });
  }, []);

  const handleCreateTestPayment = useCallback(async () => {
    setCreatingTestLink(true);
    setError(null);
    setSuccessMsg(null);
    try {
      const { paymentLink } = await adminCreateTestPayment(token);
      setTestPaymentLink(paymentLink);
      setSuccessMsg("Live $1 PayPal test payment link created.");
      await loadBookings();
    } catch (err) {
      if (err.message?.includes("Unauthorized")) {
        onAuthError();
        return;
      }
      setError(err.message || "Failed to create test payment link");
    } finally {
      setCreatingTestLink(false);
    }
  }, [token, loadBookings, onAuthError]);

  const handleCopyTestLink = useCallback(async () => {
    if (!testPaymentLink) return;
    try {
      await navigator.clipboard.writeText(testPaymentLink);
      setError(null);
      setSuccessMsg("Test payment link copied.");
    } catch {
      setError("Failed to copy test payment link");
    }
  }, [testPaymentLink]);

  const executeAction = useCallback(async () => {
    if (!confirmAction) return;
    const { bookingId, action } = confirmAction;
    setActionLoading(true);
    setConfirmAction(null);
    try {
      await adminUpdateBooking(token, bookingId, action);
      setSuccessMsg(
        action === "approve"
          ? "Booking approved! Payment link sent to guest."
          : action === "reject"
          ? "Booking rejected. Guest has been notified."
          : "Booking deleted."
      );
      await loadBookings();
    } catch (err) {
      if (err.message?.includes("Unauthorized")) {
        onAuthError();
        return;
      }
      setError(err.message || `Failed to ${action} booking`);
    } finally {
      setActionLoading(false);
    }
  }, [confirmAction, token, loadBookings, onAuthError]);

  const pending = bookings.filter((b) => b.status === "pending");
  const other = bookings.filter((b) => b.status !== "pending");

  if (loading) {
    return (
      <MKBox py={6} textAlign="center">
        <CircularProgress />
        <MKTypography variant="body2" fontWeight="bold" color="dark" mt={2}>
          Loading bookings...
        </MKTypography>
      </MKBox>
    );
  }

  return (
    <MKBox py={2}>
      {successMsg && (
        <MKAlert color="success" dismissible sx={{ mb: 2 }}>
          {successMsg}
        </MKAlert>
      )}
      {error && (
        <MKAlert color="error" dismissible sx={{ mb: 2 }}>
          {error}
        </MKAlert>
      )}

      <MKBox bgColor="grey-100" borderRadius="xl" p={2.5} mb={3}>
        <MKTypography variant="h5" mb={1}>
          <Icon sx={{ mr: 1, verticalAlign: "middle" }}>science</Icon>
          Live $1 PayPal Test
        </MKTypography>
        <MKTypography variant="body2" fontWeight="bold" color="dark" mb={2}>
          {
            "Create a real $1 payment link to test your live PayPal or card checkout. Test bookings do not block dates or send guest confirmation emails."
          }
        </MKTypography>
        <MKBox display="flex" flexWrap="wrap" gap={1} mb={testPaymentLink ? 2 : 0}>
          <MKButton
            variant="gradient"
            color="dark"
            size="small"
            disabled={creatingTestLink}
            onClick={handleCreateTestPayment}
          >
            <Icon sx={{ mr: 0.5 }}>science</Icon>
            {creatingTestLink ? "Creating..." : "Create $1 Test Payment Link"}
          </MKButton>
          {testPaymentLink && (
            <>
              <MKButton
                variant="outlined"
                color="info"
                size="small"
                onClick={() => window.open(testPaymentLink, "_blank", "noopener,noreferrer")}
              >
                <Icon sx={{ mr: 0.5 }}>open_in_new</Icon>
                Open Link
              </MKButton>
              <MKButton variant="text" color="info" size="small" onClick={handleCopyTestLink}>
                <Icon sx={{ mr: 0.5 }}>content_copy</Icon>
                Copy Link
              </MKButton>
            </>
          )}
        </MKBox>
        {testPaymentLink && (
          <MKTypography variant="body2" color="dark" sx={{ wordBreak: "break-all" }}>
            {testPaymentLink}
          </MKTypography>
        )}
      </MKBox>

      <MKBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <MKTypography variant="h5">
          <Icon sx={{ mr: 1, verticalAlign: "middle" }}>pending_actions</Icon>
          Pending Requests ({pending.length})
        </MKTypography>
        <MKButton variant="text" color="info" size="small" onClick={loadBookings}>
          <Icon sx={{ mr: 0.5 }}>refresh</Icon>
          Refresh
        </MKButton>
      </MKBox>

      {pending.length === 0 ? (
        <MKTypography variant="body2" fontWeight="bold" color="dark" py={3} textAlign="center">
          No pending booking requests.
        </MKTypography>
      ) : (
        pending.map((b) => (
          <BookingCard
            key={b.bookingId}
            booking={b}
            onAction={handleAction}
            actionLoading={actionLoading}
          />
        ))
      )}

      {other.length > 0 && (
        <>
          <Divider sx={{ my: 3 }} />
          <MKTypography variant="h5" mb={2}>
            <Icon sx={{ mr: 1, verticalAlign: "middle" }}>history</Icon>
            Past &amp; Active Bookings ({other.length})
          </MKTypography>
          {other.map((b) => (
            <BookingCard
              key={b.bookingId}
              booking={b}
              onAction={handleAction}
              actionLoading={actionLoading}
            />
          ))}
        </>
      )}

      {/* Confirmation Dialog */}
      <Dialog open={!!confirmAction} onClose={() => setConfirmAction(null)}>
        <DialogTitle>
          {confirmAction?.action === "approve"
            ? "Approve Booking?"
            : confirmAction?.action === "reject"
            ? "Reject Booking?"
            : "Delete Booking?"}
        </DialogTitle>
        <DialogContent>
          <MKTypography variant="body2" fontWeight="bold">
            {confirmAction?.action === "approve"
              ? "This will send a payment link to the guest's email. They will have to pay the deposit to confirm."
              : confirmAction?.action === "reject"
              ? "This will notify the guest that their booking request was not accepted."
              : "Are you sure you want to permanently delete this booking? This cannot be undone."}
          </MKTypography>
        </DialogContent>
        <DialogActions>
          <MKButton variant="text" color="dark" onClick={() => setConfirmAction(null)}>
            Cancel
          </MKButton>
          <MKButton
            variant="gradient"
            color={confirmAction?.action === "approve" ? "success" : "error"}
            onClick={executeAction}
          >
            {confirmAction?.action === "approve"
              ? "Approve & Send Link"
              : confirmAction?.action === "reject"
              ? "Reject"
              : "Delete Permanently"}
          </MKButton>
        </DialogActions>
      </Dialog>
    </MKBox>
  );
}

BookingCard.propTypes = {
  booking: PropTypes.object.isRequired,
  onAction: PropTypes.func.isRequired,
  actionLoading: PropTypes.bool,
};

AdminBookings.propTypes = {
  token: PropTypes.string.isRequired,
  onAuthError: PropTypes.func.isRequired,
};

export default AdminBookings;
